/**
 * useIncidents Hook - Custom hook for managing incidents
 * Epic 6: Quality/Incidents/Exceptions
 */

import { useState, useEffect, useCallback } from "react";
import {
  getIncidentsByJobId,
  getIncidentById,
  createIncident,
  updateIncidentStatus,
  addIncidentEvidence,
  attributeResponsibility,
} from "../services/incidentService";

/**
 * Custom hook for managing incidents
 * @param {string} jobId - Job identifier
 * @returns {Object} Incidents state and operations
 */
export const useIncidents = (jobId) => {
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchIncidents = useCallback(async () => {
    if (!jobId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const result = await getIncidentsByJobId(jobId);
      if (result.success) {
        setIncidents(result.data || []);
      } else {
        setError(result.error || "Failed to load incidents");
      }
    } catch (err) {
      setError(err.message || "Failed to load incidents");
    } finally {
      setLoading(false);
    }
  }, [jobId]);

  useEffect(() => {
    fetchIncidents();
  }, [fetchIncidents]);

  const create = useCallback(
    async (incidentData) => {
      const result = await createIncident(jobId, incidentData);
      if (result.success) {
        await fetchIncidents();
      }
      return result;
    },
    [jobId, fetchIncidents]
  );

  const updateStatus = useCallback(
    async (incidentId, status, context = {}) => {
      const result = await updateIncidentStatus(jobId, incidentId, status, context);
      if (result.success) {
        await fetchIncidents();
      }
      return result;
    },
    [jobId, fetchIncidents]
  );

  const addEvidence = useCallback(
    async (incidentId, file, description = "") => {
      const result = await addIncidentEvidence(jobId, incidentId, file, description);
      if (result.success) {
        await fetchIncidents();
      }
      return result;
    },
    [jobId, fetchIncidents]
  );

  const attribute = useCallback(
    async (incidentId, responsibility, actorId, reason = "") => {
      const result = await attributeResponsibility(
        jobId,
        incidentId,
        responsibility,
        actorId,
        reason
      );
      if (result.success) {
        await fetchIncidents();
      }
      return result;
    },
    [jobId, fetchIncidents]
  );

  return {
    incidents,
    loading,
    error,
    fetchIncidents,
    create,
    updateStatus,
    addEvidence,
    attribute,
  };
};

/**
 * Custom hook for managing a single incident
 * @param {string} jobId - Job identifier
 * @param {string} incidentId - Incident identifier
 * @returns {Object} Incident state and operations
 */
export const useIncident = (jobId, incidentId) => {
  const [incident, setIncident] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchIncident = useCallback(async () => {
    if (!jobId || !incidentId) {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      setError(null);
      const result = await getIncidentById(jobId, incidentId);
      if (result.success) {
        setIncident(result.data);
      } else {
        setError(result.error || "Failed to load incident");
      }
    } catch (err) {
      setError(err.message || "Failed to load incident");
    } finally {
      setLoading(false);
    }
  }, [jobId, incidentId]);

  useEffect(() => {
    fetchIncident();
  }, [fetchIncident]);

  return {
    incident,
    loading,
    error,
    fetchIncident,
  };
};
